import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Zap, Rocket, BarChart3, 
  Sparkles, ArrowRight, Check,
  ChevronLeft, ChevronRight, DollarSign, IndianRupee
} from "lucide-react";

const Package = ({ searchQuery = "" }) => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [currency, setCurrency] = useState("INR"); // INR or USD
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  const plans = [
    { 
      name: "Starter", 
      icon: <Zap size={18} className="text-[#2563EB] dark:text-[#3B82F6]" />, 
      inrMonthly: "4,999",
      inrYearly: "3,999",
      usdMonthly: "79",
      usdYearly: "59",
      description: "Ideal for solo founders, MVPs & early-stage projects",
      popular: false, 
      link: "https://hickter.com/plansapply/all-package/startup-package/",
      features: [
        "Android Mobile Application",
        "Essential High-Speed Website",
        "Free Cloud Hosting & SSL",
        "Ad Spend Growth Voucher",
        "Standard SEO Optimization",
        "Direct Email Tech Support"
      ] 
    },
    { 
      name: "Pro", 
      icon: <Rocket size={18} className="text-white" />, 
      inrMonthly: "12,999", 
      inrYearly: "9,999",
      usdMonthly: "199",
      usdYearly: "149",
      description: "Built for growing businesses scaling fast",
      popular: true, 
      link: "https://hickter.com/plansapply/all-package/business-package/",
      features: [
        "Dual Mobile Apps (iOS + Android)",
        "Custom Standard Website UI/UX",
        "Ad Campaign Growth Spend",
        "Advanced On-Page & Technical SEO",
        "Premium Brand Assets & Design",
        "24/7 Priority Engineer Support"
      ] 
    },
    { 
      name: "Enterprise", 
      icon: <BarChart3 size={18} className="text-[#2563EB] dark:text-[#3B82F6]" />, 
      inrMonthly: "31,999", 
      inrYearly: "24,999",
      usdMonthly: "499",
      usdYearly: "389",
      description: "Custom enterprise solution for massive digital scale",
      popular: false, 
      link: "https://hickter.com/plansapply/all-package/growth-package/",
      features: [
        "Full-Stack Cross-Platform Ecosystem",
        "Advanced Enterprise Web Portal",
        "Enterprise Ad Growth Package",
        "Dedicated Lead Software Engineer",
        "Unlimited Custom Feature Requests",
        "Dedicated Key Account Manager"
      ] 
    }
  ];

  const filteredPlans = plans.filter((plan) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      plan.name.toLowerCase().includes(q) ||
      plan.description.toLowerCase().includes(q) ||
      plan.features.some((f) => f.toLowerCase().includes(q))
    );
  });

  // Smooth scroll handler for mobile navigation
  const scrollToCard = (index) => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.offsetWidth * 0.85;
      scrollRef.current.scrollTo({
        left: index * cardWidth,
        behavior: "smooth"
      });
      setActiveIndex(index);
    }
  };

  // Sync state on swipe
  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollPosition = scrollRef.current.scrollLeft;
      const cardWidth = scrollRef.current.offsetWidth * 0.85;
      const newIndex = Math.round(scrollPosition / cardWidth);
      setActiveIndex(newIndex);
    }
  };

  return (
    <div className="w-full max-w-[100vw] overflow-hidden py-12 md:py-20 bg-[#F8FAFC] dark:bg-[#0B0F19] text-[#0F172A] dark:text-[#F8FAFC] font-sans flex items-center justify-center transition-colors duration-300 relative">
      
      {/* Outer Constrained Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex flex-col justify-center items-center relative z-10">
        
        {/* Section Header */}
        <header className="max-w-3xl mx-auto text-center mb-10 lg:mb-14 flex flex-col items-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center">
            
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-[#3B82F6]/15 border border-blue-200 dark:border-[#3B82F6]/30 text-blue-800 dark:text-[#3B82F6] mb-4">
              <Sparkles size={13} />
              <span className="text-xs font-bold uppercase tracking-wider">Predictable Pricing</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-3 text-[#0F172A] dark:text-[#F8FAFC]">
              Designed for Predictable <span className="text-blue-700 dark:text-[#3B82F6]">Growth</span>
            </h1>
            
            <p className="text-sm sm:text-base text-[#475569] dark:text-[#94A3B8] font-medium max-w-xl mb-6">
              Pick the tier that fits your stage today. Scale up or adjust anytime as your enterprise expands.
            </p>
            
            {/* TOGGLE CONTROLS: BILLING CYCLE & HIGHLY VISIBLE GLASSY CURRENCY SWITCHER */}
            <div className="flex flex-wrap justify-center items-center gap-4">
              
              {/* Billing Cycle Switcher */}
              <div className="flex justify-center items-center gap-2 bg-white/90 dark:bg-[#111827]/90 backdrop-blur-xl p-1.5 rounded-2xl border border-slate-300 dark:border-slate-800 shadow-md">
                <button 
                  onClick={() => setBillingCycle("monthly")}
                  className={`h-9 px-4 text-xs font-semibold rounded-xl transition-all duration-200 cursor-pointer ${
                    billingCycle === "monthly" 
                      ? "bg-blue-700 dark:bg-[#3B82F6] !text-white shadow-sm font-extrabold" 
                      : "text-slate-700 dark:text-slate-300 hover:text-[#0F172A] dark:hover:text-[#F8FAFC]"
                  }`}
                >
                  Monthly Billing
                </button>
                
                <button 
                  onClick={() => setBillingCycle("yearly")}
                  className={`h-9 px-4 text-xs font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 cursor-pointer ${
                    billingCycle === "yearly" 
                      ? "bg-blue-700 dark:bg-[#3B82F6] !text-white shadow-sm font-extrabold" 
                      : "text-slate-700 dark:text-slate-300 hover:text-[#0F172A] dark:hover:text-[#F8FAFC]"
                  }`}
                >
                  <span>Annual Billing</span>
                  <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                    billingCycle === "yearly" 
                      ? "bg-white text-blue-800" 
                      : "bg-blue-100 dark:bg-[#3B82F6]/20 text-blue-800 dark:text-[#3B82F6]"
                  }`}>
                    Save 20%
                  </span>
                </button>
              </div>

              {/* HIGHLY VISIBLE GLASSY CURRENCY SWITCHER BUTTONS (INR ₹ / USD $) */}
              <div className="inline-flex items-center gap-2 p-1.5 rounded-2xl bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-slate-300 dark:border-slate-700 shadow-lg shadow-blue-500/10">
                <button
                  onClick={() => setCurrency("INR")}
                  className={`h-9 px-4 text-xs sm:text-sm font-bold rounded-xl transition-all duration-200 flex items-center gap-1.5 cursor-pointer ${
                    currency === "INR"
                      ? "bg-blue-600 dark:bg-blue-500 !text-white font-extrabold shadow-md shadow-blue-500/30 border border-blue-600 dark:border-blue-400"
                      : "text-slate-800 dark:text-slate-200 bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 hover:text-blue-700 dark:hover:text-blue-400 border border-slate-300 dark:border-slate-700"
                  }`}
                >
                  <IndianRupee size={14} className={currency === "INR" ? "!text-white" : "text-blue-600 dark:text-blue-400"} />
                  <span className={currency === "INR" ? "!text-white font-extrabold" : "text-slate-800 dark:text-slate-200 font-bold"}>INR (₹)</span>
                </button>

                <button
                  onClick={() => setCurrency("USD")}
                  className={`h-9 px-4 text-xs sm:text-sm font-bold rounded-xl transition-all duration-200 flex items-center gap-1.5 cursor-pointer ${
                    currency === "USD"
                      ? "bg-blue-600 dark:bg-blue-500 !text-white font-extrabold shadow-md shadow-blue-500/30 border border-blue-600 dark:border-blue-400"
                      : "text-slate-800 dark:text-slate-200 bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 hover:text-blue-700 dark:hover:text-blue-400 border border-slate-300 dark:border-slate-700"
                  }`}
                >
                  <DollarSign size={14} className={currency === "USD" ? "!text-white" : "text-blue-600 dark:text-blue-400"} />
                  <span className={currency === "USD" ? "!text-white font-extrabold" : "text-slate-800 dark:text-slate-200 font-bold"}>USD ($)</span>
                </button>
              </div>

            </div>

          </motion.div>
        </header>

        {/* RESPONSIVE CAROUSEL & DESKTOP GRID CONTAINER */}
        <main className="w-full">
          <div 
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x snap-mandatory gap-6 px-4 pb-8 w-full scrollbar-none [&::-webkit-scrollbar]:hidden lg:grid lg:grid-cols-3 lg:gap-8 lg:overflow-visible lg:px-0 cursor-grab active:cursor-grabbing"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {filteredPlans.map((plan, i) => {
              const displayPrice = currency === "INR" 
                ? (billingCycle === "monthly" ? plan.inrMonthly : plan.inrYearly)
                : (billingCycle === "monthly" ? plan.usdMonthly : plan.usdYearly);
              
              const currencySymbol = currency === "INR" ? "₹" : "$";

              return (
                <motion.div 
                  key={plan.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative w-[85vw] max-w-[340px] shrink-0 snap-center lg:w-full lg:max-w-none h-full min-h-fit rounded-2xl transition-all duration-300 flex flex-col justify-between ${
                    plan.popular 
                      ? 'p-6 sm:p-8 lg:p-10 lg:-translate-y-2 bg-white dark:bg-[#111827] text-[#0F172A] dark:text-[#F8FAFC] border-2 border-blue-600 dark:border-[#3B82F6] shadow-xl shadow-blue-500/15 z-10' 
                      : 'p-6 sm:p-8 bg-white dark:bg-[#111827] text-[#0F172A] dark:text-[#F8FAFC] border border-[#E2E8F0] dark:border-[#1F2937] shadow-sm hover:border-blue-600/50 dark:hover:border-[#3B82F6]/50'
                  }`}
                >
                  {/* Most Popular Badge */}
                  {plan.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-blue-700 dark:bg-[#3B82F6] !text-white px-4 py-1 rounded-full text-xs font-extrabold uppercase tracking-widest whitespace-nowrap shadow-md flex items-center gap-1.5 z-20">
                      <Sparkles size={12} className="fill-current !text-white" />
                      <span className="!text-white">Most Popular</span>
                    </div>
                  )}
                  
                  {/* INTERNAL CARD FLEX-GROW CONTENT */}
                  <div className="flex-grow flex flex-col justify-between">
                    <div>
                      {/* Card Icon & Tier */}
                      <div className="flex justify-between items-center mb-4">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                          plan.popular 
                            ? 'bg-blue-700 dark:bg-[#3B82F6] !text-white shadow-md' 
                            : 'bg-[#F8FAFC] dark:bg-[#1F2937] border border-[#E2E8F0] dark:border-[#1F2937]'
                        }`}>
                          {plan.icon}
                        </div>
                        <span className="text-xs font-extrabold uppercase tracking-widest text-[#475569] dark:text-[#94A3B8]">
                          Tier 0{i + 1}
                        </span>
                      </div>

                      <h3 className="text-2xl font-extrabold text-[#0F172A] dark:text-[#F8FAFC] tracking-tight mb-1.5">
                        {plan.name}
                      </h3>
                      
                      <p className="text-xs sm:text-sm text-[#475569] dark:text-[#94A3B8] font-medium leading-relaxed min-h-[40px]">
                        {plan.description}
                      </p>

                      {/* Price Block */}
                      <div className="my-6 pb-6 border-b border-[#E2E8F0] dark:border-[#1F2937]">
                        <div className="flex items-baseline gap-1">
                          <span className="text-base font-bold text-blue-700 dark:text-[#3B82F6]">{currencySymbol}</span>
                          <AnimatePresence mode="wait">
                            <motion.span
                              key={`${billingCycle}-${currency}`}
                              initial={{ opacity: 0, y: 3 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -3 }}
                              className="text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0F172A] dark:text-[#F8FAFC]"
                            >
                              {displayPrice}
                            </motion.span>
                          </AnimatePresence>
                          <span className="text-xs sm:text-sm font-semibold text-[#475569] dark:text-[#94A3B8]">
                            /{billingCycle === 'monthly' ? 'month' : 'month billed annually'}
                          </span>
                        </div>
                      </div>

                      {/* Feature List */}
                      <div className="space-y-4 my-6">
                        <p className="text-xs font-extrabold uppercase tracking-wider text-[#475569] dark:text-[#94A3B8]">
                          Key Features
                        </p>
                        {plan.features.map((feat, idx) => (
                          <div key={idx} className="flex gap-3 items-center">
                            <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-[#3B82F6]/20 flex items-center justify-center shrink-0">
                              <Check size={12} strokeWidth={3} className="text-blue-700 dark:text-[#3B82F6]" />
                            </div>
                            <span className="text-xs sm:text-sm font-semibold text-[#0F172A] dark:text-[#F8FAFC] leading-snug">
                              {feat}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* UNIFIED PINNED CTA BUTTON AT ABSOLUTE BOTTOM */}
                  <Link 
                    to={`/apply/package?plan=${encodeURIComponent(plan.name)}`}
                    className="w-full mt-auto h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-[#3B82F6] dark:hover:bg-blue-600 !text-white shadow-md cursor-pointer transition-all"
                  >
                    <span className="!text-white font-bold">Apply Now for {plan.name}</span>
                    <ArrowRight size={16} className="!text-white" />
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* Mobile Navigation Controls */}
          <div className="flex lg:hidden items-center justify-between w-full max-w-[85%] mx-auto mt-2">
            <button
              onClick={() => scrollToCard(Math.max(0, activeIndex - 1))}
              disabled={activeIndex === 0}
              className={`p-2.5 rounded-full border border-[#E2E8F0] dark:border-[#1F2937] bg-white dark:bg-[#111827] shadow-sm transition-all ${
                activeIndex === 0 ? "opacity-30 cursor-not-allowed" : "hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-95"
              }`}
              aria-label="Previous Plan"
            >
              <ChevronLeft size={18} className="text-[#0F172A] dark:text-[#F8FAFC]" />
            </button>

            {/* Pagination Dots */}
            <div className="flex gap-2">
              {filteredPlans.map((_, i) => (
                <button
                  key={i}
                  onClick={() => scrollToCard(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    activeIndex === i ? "w-6 bg-blue-700 dark:bg-[#3B82F6]" : "w-2 bg-[#CBD5E1] dark:bg-[#1F2937]"
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={() => scrollToCard(Math.min(filteredPlans.length - 1, activeIndex + 1))}
              disabled={activeIndex === filteredPlans.length - 1}
              className={`p-2.5 rounded-full border border-[#E2E8F0] dark:border-[#1F2937] bg-white dark:bg-[#111827] shadow-sm transition-all ${
                activeIndex === filteredPlans.length - 1 ? "opacity-30 cursor-not-allowed" : "hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-95"
              }`}
              aria-label="Next Plan"
            >
              <ChevronRight size={18} className="text-[#0F172A] dark:text-[#F8FAFC]" />
            </button>
          </div>

        </main>
        
      </div>

    </div>
  );
};

export default Package;